/**
 * Main App Component - Multi-game routing and auth guards
 */

import { lazy, Suspense, useEffect } from "react";
import { getGameType } from "../game-registry";
import {
  buildGamePath,
  buildGameRoomPath,
  buildLobbyPath,
  navigateTo,
  type RouteMode,
  useRoute,
} from "../router";
import { GameRoomScreen } from "../shared/components/GameRoomScreen";
import { GuestJoinScreen } from "../shared/components/GuestJoinScreen";
import { GuestLoginScreen } from "../shared/components/GuestLoginScreen";
import { LobbyScreen } from "../shared/components/LobbyScreen";
import { LoginScreen } from "../shared/components/LoginScreen";
import { RegisterScreen } from "../shared/components/RegisterScreen";
import { ReplayLobbyScreen } from "../shared/components/ReplayLobbyScreen";
import { ReplayRoomScreen } from "../shared/components/ReplayRoomScreen";
import { getConfig } from "../shared/config/runtime-config";
import { useAuth, useAuthActions } from "../shared/store/auth-store";
import { useLobby, useLobbyActions } from "../shared/store/lobby-store";
import { wsActions } from "../shared/store/ws-store";
import { ErrorBoundary } from "./ErrorBoundary";
import { HomeScreen } from "./HomeScreen";

const HorrorRaceReplayScreen = lazy(() =>
  import("../games/horror-race/ReplayScreen").then((m) => ({
    default: m.ReplayScreen,
  })),
);
const FruitShopReplayScreen = lazy(() =>
  import("../games/fruit-shop/ReplayScreen").then((m) => ({
    default: m.ReplayScreen,
  })),
);
const FishShopReplayScreen = lazy(() =>
  import("../games/fish-shop/ReplayScreen").then((m) => ({
    default: m.ReplayScreen,
  })),
);
const BlackAndWhiteReplayScreen = lazy(() =>
  import("../games/black-and-white/ReplayScreen").then((m) => ({
    default: m.ReplayScreen,
  })),
);
const IndianPokerReplayScreen = lazy(() =>
  import("../games/indian-poker/ReplayScreen").then((m) => ({
    default: m.ReplayScreen,
  })),
);
const YinshReplayScreen = lazy(() =>
  import("../games/yinsh/ReplayScreen").then((m) => ({
    default: m.ReplayScreen,
  })),
);
const CascadiaReplayScreen = lazy(() =>
  import("../games/cascadia/ReplayScreen").then((m) => ({
    default: m.ReplayScreen,
  })),
);
const AugmentedWarReplayScreen = lazy(() =>
  import("../games/augmented-war/ReplayScreen").then((m) => ({
    default: m.ReplayScreen,
  })),
);
const AugmentedStoneFlickingReplayScreen = lazy(() =>
  import("../games/augmented-stone-flicking/ReplayScreen").then((m) => ({
    default: m.ReplayScreen,
  })),
);
const TriAngerReplayScreen = lazy(() =>
  import("../games/tri-anger/ReplayScreen").then((m) => ({
    default: m.ReplayScreen,
  })),
);
const ChessReplayScreen = lazy(() =>
  import("../games/chess/ReplayScreen").then((m) => ({
    default: m.ReplayScreen,
  })),
);
const YachtReplayScreen = lazy(() =>
  import("../games/yacht/ReplayScreen").then((m) => ({
    default: m.ReplayScreen,
  })),
);
const RunHorsesReplayScreen = lazy(() =>
  import("../games/run-horses/ReplayScreen").then((m) => ({
    default: m.ReplayScreen,
  })),
);
const CarnivalOfSinsReplayScreen = lazy(() =>
  import("../games/carnival-of-sins/ReplayScreen").then((m) => ({
    default: m.ReplayScreen,
  })),
);
const MemoryFeastReplayScreen = lazy(() =>
  import("../games/memory-feast/ReplayScreen").then((m) => ({
    default: m.ReplayScreen,
  })),
);
const OblivionMineReplayScreen = lazy(() =>
  import("../games/oblivion-mine/ReplayScreen").then((m) => ({
    default: m.ReplayScreen,
  })),
);
const ShowMeTheCoinReplayScreen = lazy(() =>
  import("../games/show-me-the-coin/ReplayScreen").then((m) => ({
    default: m.ReplayScreen,
  })),
);
const CubeHellReplayScreen = lazy(() =>
  import("../games/cube-hell/ReplayScreen").then((m) => ({
    default: m.ReplayScreen,
  })),
);
const LoveWinsAllReplayScreen = lazy(() =>
  import("../games/love-wins-all/ReplayScreen").then((m) => ({
    default: m.ReplayScreen,
  })),
);
const OneNightUltimateWerewolfReplayScreen = lazy(() =>
  import("../games/one-night-ultimate-werewolf/ReplayScreen").then((m) => ({
    default: m.ReplayScreen,
  })),
);

interface GameErrorFallbackProps {
  readonly gameType: string;
  readonly routeMode: RouteMode;
}

function GameErrorFallback({ gameType, routeMode }: GameErrorFallbackProps) {
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-sm rounded-xl border border-border-primary bg-surface-primary p-8 text-center shadow-sm">
        <p className="mb-2 font-semibold text-lg text-text-primary">
          게임 오류가 발생했습니다
        </p>
        <p className="mb-6 text-sm text-text-secondary">
          게임 화면을 불러오는 중 문제가 발생했습니다.
        </p>
        <button
          type="button"
          className="cursor-pointer rounded-md border-none bg-accent-blue px-6 py-2 font-semibold text-sm text-white transition-colors hover:bg-accent-blue-hover"
          onClick={() =>
            navigateTo(buildLobbyPath(gameType, routeMode), { replace: true })
          }
        >
          로비로 돌아가기
        </button>
      </div>
    </div>
  );
}

export function App() {
  const route = useRoute();
  const { isAuthenticated, isLoading } = useAuth();
  const authActions = useAuthActions();
  const { currentRoom } = useLobby();
  const lobbyActions = useLobbyActions();

  const routeGameType =
    route.name === "game" ||
    route.name === "gameRoom" ||
    route.name === "gameReplay" ||
    route.name === "lobby"
      ? route.gameType
      : null;

  const routeGameId =
    route.name === "game" || route.name === "gameRoom" ? route.gameId : null;
  const routeMode: RouteMode =
    "routeMode" in route && route.routeMode === "v2" ? "v2" : "legacy";

  // Initialize auth on mount
  useEffect(() => {
    if (route.name === "gameReplay" || route.name === "replayRoom") return;
    authActions.initialize();
  }, [authActions.initialize, route.name]);

  // Connect WebSocket when authenticated, disconnect when logged out
  useEffect(() => {
    if (isAuthenticated) {
      wsActions.connect(getConfig().multiplayerServiceUrl, routeMode);
    } else {
      wsActions.disconnect();
    }
  }, [isAuthenticated, routeMode]);

  // Route guards
  useEffect(() => {
    if (isLoading) return;

    if (
      !isAuthenticated &&
      route.name !== "login" &&
      route.name !== "guestLogin" &&
      route.name !== "register" &&
      route.name !== "guestJoin" &&
      route.name !== "gameReplay" &&
      route.name !== "replayRoom"
    ) {
      navigateTo("/login", { replace: true });
    }

    if (
      isAuthenticated &&
      (route.name === "login" || route.name === "guestLogin")
    ) {
      navigateTo("/", { replace: true });
    }

    if (route.name === "notFound") {
      navigateTo(isAuthenticated ? "/" : "/login", { replace: true });
    }
  }, [isAuthenticated, isLoading, route.name]);

  // Route recovery: redirect to correct view based on room status,
  // or fetch room from server if not in store (e.g., page reload).
  useEffect(() => {
    if (!routeGameId || !routeGameType) return;
    if (!isAuthenticated) return;

    if (currentRoom?.id === routeGameId) {
      if (route.name === "gameRoom" && currentRoom.status !== "WAITING") {
        navigateTo(buildGamePath(routeGameType, routeGameId, routeMode), {
          replace: true,
        });
      }
      if (route.name === "game" && currentRoom.status === "WAITING") {
        navigateTo(buildGameRoomPath(routeGameType, routeGameId, routeMode), {
          replace: true,
        });
      }
      return;
    }

    // Ensure API client is initialized before fetching room
    lobbyActions.setGameType(routeGameType, routeMode);

    // No matching room in store -- try to fetch from server
    lobbyActions
      .fetchRoom(routeGameId)
      .then((room) => {
        if (!room) {
          navigateTo(
            routeGameType ? buildLobbyPath(routeGameType, routeMode) : "/",
            { replace: true },
          );
        }
      })
      .catch(() => {
        navigateTo(
          routeGameType ? buildLobbyPath(routeGameType, routeMode) : "/",
          { replace: true },
        );
      });
  }, [
    currentRoom?.id,
    currentRoom?.status,
    isAuthenticated,
    lobbyActions.fetchRoom,
    lobbyActions.setGameType,
    route.name,
    routeMode,
    routeGameId,
    routeGameType,
  ]);

  // Read-only replay routes (/<game>/replay/<scenario>) are backend-free
  // (L3 in docs/guides/board-game-test-strategy.md) and are verified
  // post-deploy by board-game-post-deploy-e2e.yml.
  if (route.name === "gameReplay") {
    const fallback = (
      <div className="flex min-h-screen items-center justify-center">
        불러오는 중...
      </div>
    );
    if (route.gameType === "horror-race") {
      return (
        <Suspense fallback={fallback}>
          <ErrorBoundary
            fallback={
              <GameErrorFallback
                gameType={route.gameType}
                routeMode={routeMode}
              />
            }
          >
            <HorrorRaceReplayScreen scenario={route.scenario} />
          </ErrorBoundary>
        </Suspense>
      );
    }
    if (route.gameType === "fruit-shop") {
      return (
        <Suspense fallback={fallback}>
          <ErrorBoundary
            fallback={
              <GameErrorFallback
                gameType={route.gameType}
                routeMode={routeMode}
              />
            }
          >
            <FruitShopReplayScreen scenario={route.scenario} />
          </ErrorBoundary>
        </Suspense>
      );
    }
    if (route.gameType === "fish-shop") {
      return (
        <Suspense fallback={fallback}>
          <ErrorBoundary
            fallback={
              <GameErrorFallback
                gameType={route.gameType}
                routeMode={routeMode}
              />
            }
          >
            <FishShopReplayScreen scenario={route.scenario} />
          </ErrorBoundary>
        </Suspense>
      );
    }
    if (route.gameType === "black-and-white") {
      return (
        <Suspense fallback={fallback}>
          <ErrorBoundary
            fallback={
              <GameErrorFallback
                gameType={route.gameType}
                routeMode={routeMode}
              />
            }
          >
            <BlackAndWhiteReplayScreen scenario={route.scenario} />
          </ErrorBoundary>
        </Suspense>
      );
    }
    if (route.gameType === "indian-poker") {
      return (
        <Suspense fallback={fallback}>
          <ErrorBoundary
            fallback={
              <GameErrorFallback
                gameType={route.gameType}
                routeMode={routeMode}
              />
            }
          >
            <IndianPokerReplayScreen scenario={route.scenario} />
          </ErrorBoundary>
        </Suspense>
      );
    }
    if (route.gameType === "yinsh") {
      return (
        <Suspense fallback={fallback}>
          <ErrorBoundary
            fallback={
              <GameErrorFallback
                gameType={route.gameType}
                routeMode={routeMode}
              />
            }
          >
            <YinshReplayScreen scenario={route.scenario} />
          </ErrorBoundary>
        </Suspense>
      );
    }
    if (route.gameType === "cascadia") {
      return (
        <Suspense fallback={fallback}>
          <ErrorBoundary
            fallback={
              <GameErrorFallback
                gameType={route.gameType}
                routeMode={routeMode}
              />
            }
          >
            <CascadiaReplayScreen scenario={route.scenario} />
          </ErrorBoundary>
        </Suspense>
      );
    }
    if (route.gameType === "augmented-war") {
      return (
        <Suspense fallback={fallback}>
          <ErrorBoundary
            fallback={
              <GameErrorFallback
                gameType={route.gameType}
                routeMode={routeMode}
              />
            }
          >
            <AugmentedWarReplayScreen scenario={route.scenario} />
          </ErrorBoundary>
        </Suspense>
      );
    }
    if (route.gameType === "augmented-stone-flicking") {
      return (
        <Suspense fallback={fallback}>
          <ErrorBoundary
            fallback={
              <GameErrorFallback
                gameType={route.gameType}
                routeMode={routeMode}
              />
            }
          >
            <AugmentedStoneFlickingReplayScreen scenario={route.scenario} />
          </ErrorBoundary>
        </Suspense>
      );
    }
    if (route.gameType === "tri-anger") {
      return (
        <Suspense fallback={fallback}>
          <ErrorBoundary
            fallback={
              <GameErrorFallback
                gameType={route.gameType}
                routeMode={routeMode}
              />
            }
          >
            <TriAngerReplayScreen scenario={route.scenario} />
          </ErrorBoundary>
        </Suspense>
      );
    }
    if (route.gameType === "chess") {
      return (
        <Suspense fallback={fallback}>
          <ErrorBoundary
            fallback={
              <GameErrorFallback
                gameType={route.gameType}
                routeMode={routeMode}
              />
            }
          >
            <ChessReplayScreen scenario={route.scenario} />
          </ErrorBoundary>
        </Suspense>
      );
    }
    if (route.gameType === "yacht" || route.gameType === "yacht-race") {
      return (
        <Suspense fallback={fallback}>
          <ErrorBoundary
            fallback={
              <GameErrorFallback
                gameType={route.gameType}
                routeMode={routeMode}
              />
            }
          >
            <YachtReplayScreen scenario={route.scenario} />
          </ErrorBoundary>
        </Suspense>
      );
    }
    if (route.gameType === "run-horses") {
      return (
        <Suspense fallback={fallback}>
          <ErrorBoundary
            fallback={
              <GameErrorFallback
                gameType={route.gameType}
                routeMode={routeMode}
              />
            }
          >
            <RunHorsesReplayScreen scenario={route.scenario} />
          </ErrorBoundary>
        </Suspense>
      );
    }
    if (route.gameType === "carnival-of-sins") {
      return (
        <Suspense fallback={fallback}>
          <ErrorBoundary
            fallback={
              <GameErrorFallback
                gameType={route.gameType}
                routeMode={routeMode}
              />
            }
          >
            <CarnivalOfSinsReplayScreen scenario={route.scenario} />
          </ErrorBoundary>
        </Suspense>
      );
    }
    if (route.gameType === "memory-feast") {
      return (
        <Suspense fallback={fallback}>
          <ErrorBoundary
            fallback={
              <GameErrorFallback
                gameType={route.gameType}
                routeMode={routeMode}
              />
            }
          >
            <MemoryFeastReplayScreen scenario={route.scenario} />
          </ErrorBoundary>
        </Suspense>
      );
    }
    if (route.gameType === "oblivion-mine") {
      return (
        <Suspense fallback={fallback}>
          <ErrorBoundary
            fallback={
              <GameErrorFallback
                gameType={route.gameType}
                routeMode={routeMode}
              />
            }
          >
            <OblivionMineReplayScreen scenario={route.scenario} />
          </ErrorBoundary>
        </Suspense>
      );
    }
    if (route.gameType === "show-me-the-coin") {
      return (
        <Suspense fallback={fallback}>
          <ErrorBoundary
            fallback={
              <GameErrorFallback
                gameType={route.gameType}
                routeMode={routeMode}
              />
            }
          >
            <ShowMeTheCoinReplayScreen scenario={route.scenario} />
          </ErrorBoundary>
        </Suspense>
      );
    }
    if (route.gameType === "love-wins-all") {
      return (
        <Suspense fallback={fallback}>
          <ErrorBoundary
            fallback={
              <GameErrorFallback
                gameType={route.gameType}
                routeMode={routeMode}
              />
            }
          >
            <LoveWinsAllReplayScreen scenario={route.scenario} />
          </ErrorBoundary>
        </Suspense>
      );
    }
    if (route.gameType === "cube-hell") {
      return (
        <Suspense fallback={fallback}>
          <ErrorBoundary
            fallback={
              <GameErrorFallback
                gameType={route.gameType}
                routeMode={routeMode}
              />
            }
          >
            <CubeHellReplayScreen scenario={route.scenario} />
          </ErrorBoundary>
        </Suspense>
      );
    }
    if (route.gameType === "one-night-ultimate-werewolf") {
      return (
        <Suspense fallback={fallback}>
          <ErrorBoundary
            fallback={
              <GameErrorFallback
                gameType={route.gameType}
                routeMode={routeMode}
              />
            }
          >
            <OneNightUltimateWerewolfReplayScreen scenario={route.scenario} />
          </ErrorBoundary>
        </Suspense>
      );
    }
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-text-secondary">리플레이 화면 준비 중...</p>
      </div>
    );
  }

  // Replay rooms are public and backend-free to render — handled before the
  // auth-loading gate (auth init is skipped for this route).
  if (route.name === "replayRoom") {
    return <ReplayRoomScreen shareCode={route.shareCode} />;
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-text-primary">
        불러오는 중...
      </div>
    );
  }

  if (route.name === "guestJoin") {
    return (
      <GuestJoinScreen
        gameType={route.gameType}
        roomNumber={route.roomNumber}
        secret={route.secret}
        seat={route.seat}
      />
    );
  }

  if (!isAuthenticated) {
    const REGISTER_ENABLED = getConfig().enableRegister;
    if (route.name === "register" && REGISTER_ENABLED) {
      return <RegisterScreen />;
    }
    if (route.name === "guestLogin") {
      return <GuestLoginScreen />;
    }
    return <LoginScreen />;
  }

  if (route.name === "replayLobby") {
    return <ReplayLobbyScreen />;
  }

  if (route.name === "gameRoom" && routeGameType) {
    return <GameRoomScreen gameType={routeGameType} routeMode={routeMode} />;
  }

  if (route.name === "game" && routeGameType) {
    const config = getGameType(routeGameType);
    if (config?.GameScreen) {
      const Screen = config.GameScreen;
      return (
        <Suspense
          fallback={
            <div className="flex min-h-screen items-center justify-center">
              불러오는 중...
            </div>
          }
        >
          <ErrorBoundary
            fallback={
              <GameErrorFallback
                gameType={routeGameType}
                routeMode={routeMode}
              />
            }
          >
            <Screen />
          </ErrorBoundary>
        </Suspense>
      );
    }
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-text-secondary">게임 화면 준비 중...</p>
      </div>
    );
  }

  if (route.name === "lobby" && routeGameType) {
    return <LobbyScreen gameType={routeGameType} routeMode={routeMode} />;
  }

  return <HomeScreen />;
}
